import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface DashboardHeaderProps {
  onMenuClick: () => void;
}

const DashboardHeader = ({ onMenuClick }: DashboardHeaderProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [showScheduleMeetingModal, setShowScheduleMeetingModal] = useState(false);
  const [newMeetingData, setNewMeetingData] = useState({
    title: '',
    client: '',
    date: '',
    time: '',
    duration: '60',
    location: '',
    type: 'in-person',
    description: '',
    attendees: ''
  });

  // Load notifications from localStorage on component mount
  useEffect(() => {
    const savedNotifications = localStorage.getItem('notifications');
    if (savedNotifications) {
      setNotifications(JSON.parse(savedNotifications));
    } else {
      // Initialize with default notifications
      const defaultNotifications = [
        {
          id: '1',
          type: 'meeting',
          title: 'Upcoming Meeting',
          message: 'Team standup in 30 minutes',
          time: '10 minutes ago',
          read: false,
          projectId: '1',
          category: 'private-clients'
        },
        {
          id: '2',
          type: 'project',
          title: 'Project Update',
          message: 'Sunset Villa Renovation progress updated',
          time: '1 hour ago',
          read: false,
          projectId: '1',
          category: 'private-clients'
        },
        {
          id: '3',
          type: 'deadline',
          title: 'Deadline Reminder',
          message: 'Maple Street Apartments due in 3 days',
          time: '2 hours ago',
          read: false,
          projectId: '2',
          category: 'private-clients'
        },
        {
          id: '4',
          type: 'invoice',
          title: 'Invoice Generated',
          message: 'Invoice #INV-2024-001 ready for review',
          time: '3 hours ago',
          read: false,
          projectId: '6',
          category: 'cic'
        },
        {
          id: '5',
          type: 'task',
          title: 'Task Completed',
          message: 'Security system installation completed',
          time: '5 hours ago',
          read: false,
          projectId: '4',
          category: 'securitas'
        }
      ];
      setNotifications(defaultNotifications);
      localStorage.setItem('notifications', JSON.stringify(defaultNotifications));
    }
  }, []);

  // Save notifications to localStorage whenever notifications change
  useEffect(() => {
    localStorage.setItem('notifications', JSON.stringify(notifications));
  }, [notifications]);

  // Real project data for search
  const projects = [
    { id: '1', name: 'Sunset Villa Renovation', client: 'Johnson Family', status: 'In Progress', budget: '$125,000', type: 'Residential' },
    { id: '2', name: 'Downtown Office Complex', client: 'TechCorp Inc.', status: 'Planning', budget: '$850,000', type: 'Commercial' },
    { id: '3', name: 'Maple Street Apartments', client: 'City Housing Authority', status: 'In Progress', budget: '$450,000', type: 'Residential' },
    { id: '4', name: 'Industrial Warehouse', client: 'LogiCorp', status: 'Completed', budget: '$320,000', type: 'Industrial' },
    { id: '5', name: 'Retail Store Renovation', client: 'Fashion Forward', status: 'On Hold', budget: '$75,000', type: 'Commercial' }
  ];

  const leads = [
    { id: '1', name: 'Sarah Wilson', company: 'Wilson Enterprises', type: 'Commercial', value: '$200,000' },
    { id: '2', name: 'Mike Chen', company: 'Chen Holdings', type: 'Residential', value: '$150,000' },
    { id: '3', name: 'Lisa Rodriguez', company: 'Rodriguez Corp', type: 'Industrial', value: '$500,000' }
  ];

  const tasks = [
    { id: '1', title: 'Review architectural plans', project: 'Sunset Villa Renovation', priority: 'High' },
    { id: '2', title: 'Schedule site inspection', project: 'Downtown Office Complex', priority: 'Medium' },
    { id: '3', title: 'Update project timeline', project: 'Maple Street Apartments', priority: 'High' }
  ];

  const searchData = [
    // Projects with proper category mapping
    { id: '1', name: 'Sunset Villa Renovation', type: 'project', category: 'private-clients', client: 'Johnson Family' },
    { id: '2', name: 'Maple Street Apartments', type: 'project', category: 'private-clients', client: 'City Housing Authority' },
    { id: '3', name: 'Modern Family Home', type: 'project', category: 'private-clients', client: 'Smith Family' },
    { id: '4', name: 'Security Command Center', type: 'project', category: 'securitas', client: 'Securitas AB' },
    { id: '5', name: 'Perimeter Fence Installation', type: 'project', category: 'securitas', client: 'Securitas Regional' },
    { id: '6', name: 'Downtown Office Complex', type: 'project', category: 'cic', client: 'TechCorp Inc.' },
    { id: '7', name: 'Industrial Warehouse', type: 'project', category: 'cic', client: 'LogiCorp' },
    { id: '8', name: 'Retail Store Renovation', type: 'project', category: 'cic', client: 'Fashion Forward' },
    { id: '9', name: 'HVAC System Upgrade', type: 'project', category: 'johnson-controls', client: 'Johnson Controls' },
    { id: '10', name: 'Building Automation Install', type: 'project', category: 'johnson-controls', client: 'Johnson Controls' },
    { id: '11', name: 'Historic Building Restoration', type: 'project', category: 'others', client: 'Heritage Foundation' },
    { id: '12', name: 'Emergency Repair Project', type: 'project', category: 'others', client: 'City Emergency Services' },
    // Leads
    { id: 'l1', name: 'Potential Office Renovation', type: 'lead', status: 'New' },
    { id: 'l2', name: 'Residential Kitchen Remodel', type: 'lead', status: 'Contacted' },
    // Tasks
    { id: 't1', name: 'Review blueprints', type: 'task', status: 'Pending' },
    { id: 't2', name: 'Schedule site inspection', type: 'task', status: 'In Progress' }
  ];

  const unreadCount = notifications.filter(n => !n.read).length;

  // Search functionality
  const handleSearch = (query: string) => {
    setSearchQuery(query);
    
    if (query.trim() === '') {
      setSearchResults([]);
      return;
    }

    // Mock search results - in production, this would be an API call
    const mockResults = [
      { 
        id: '1', 
        title: 'Sunset Villa Renovation', 
        type: 'Project', 
        category: 'private-clients',
        description: 'Residential renovation project',
        icon: 'ri-home-4-line'
      },
      { 
        id: '2', 
        title: 'Maple Street Apartments', 
        type: 'Project', 
        category: 'private-clients',
        description: 'Multi-unit residential project',
        icon: 'ri-building-line'
      },
      { 
        id: '6', 
        title: 'Downtown Office Complex', 
        type: 'Project', 
        category: 'cic',
        description: 'Commercial office development',
        icon: 'ri-building-2-line'
      },
      { 
        id: '7', 
        title: 'Industrial Warehouse', 
        type: 'Project', 
        category: 'cic',
        description: 'Industrial facility construction',
        icon: 'ri-store-line'
      },
      { 
        id: 'lead-1', 
        title: 'New Client Inquiry', 
        type: 'Lead',
        description: 'Potential residential project',
        icon: 'ri-user-add-line'
      },
      { 
        id: 'task-1', 
        title: 'Review Building Permits', 
        type: 'Task',
        description: 'Urgent task for Sunset Villa',
        icon: 'ri-task-line'
      }
    ];

    const filtered = mockResults.filter(item =>
      item.title.toLowerCase().includes(query.toLowerCase()) ||
      item.description.toLowerCase().includes(query.toLowerCase())
    );

    setSearchResults(filtered);
    setShowResults(true);
  };

  const getSearchResults = () => {
    if (!searchQuery) return [];
    
    const query = searchQuery.toLowerCase();
    const results = [];

    // Search projects
    const matchingProjects = projects.filter(project => 
      project.name.toLowerCase().includes(query) ||
      project.client.toLowerCase().includes(query) ||
      project.type.toLowerCase().includes(query) ||
      project.status.toLowerCase().includes(query)
    );
    results.push(...matchingProjects.map(p => ({ ...p, category: 'Projects' })));

    // Search leads
    const matchingLeads = leads.filter(lead =>
      lead.name.toLowerCase().includes(query) ||
      lead.company.toLowerCase().includes(query) ||
      lead.type.toLowerCase().includes(query)
    );
    results.push(...matchingLeads.map(l => ({ ...l, category: 'Leads' })));

    // Search tasks
    const matchingTasks = tasks.filter(task =>
      task.title.toLowerCase().includes(query) ||
      task.project.toLowerCase().includes(query)
    );
    results.push(...matchingTasks.map(t => ({ ...t, category: 'Tasks' })));

    return results.slice(0, 8);
  };

  const handleSearchResultClick = (result: any) => {
    setShowResults(false);
    setSearchQuery('');
    
    // Navigate based on result category with specific IDs
    if (result.category === 'Projects') {
      if (window.REACT_APP_NAVIGATE) {
        // Navigate to specific project details
        const projectType = result.type.toLowerCase().replace(' ', '-');
        window.REACT_APP_NAVIGATE(`/projects/${projectType}?id=${result.id}`);
      }
    } else if (result.category === 'Leads') {
      if (window.REACT_APP_NAVIGATE) {
        window.REACT_APP_NAVIGATE(`/leads?id=${result.id}`);
      }
    } else if (result.category === 'Tasks') {
      if (window.REACT_APP_NAVIGATE) {
        window.REACT_APP_NAVIGATE(`/dashboard?task=${result.id}`);
      }
    }
  };

  const handleResultClick = (result: any) => {
    setShowResults(false);
    setSearchQuery('');
    setSearchResults([]);

    // Navigate based on result type
    if (result.type === 'Project' && result.category) {
      // Navigate to specific project details page with category and project ID
      navigate(`/projects/details/${result.category}?projectId=${result.id}`);
    } else if (result.type === 'Lead') {
      navigate('/leads');
    } else if (result.type === 'Task') {
      navigate('/dashboard');
    }
  };

  const handleCreateMeeting = () => {
    if (newMeetingData.title && newMeetingData.date && newMeetingData.time) {
      const newMeeting = {
        id: Date.now().toString(),
        ...newMeetingData
      };
      
      // Store meeting in localStorage for persistence across pages
      const existingMeetings = JSON.parse(localStorage.getItem('teamsdayMeetings') || '[]');
      existingMeetings.push(newMeeting);
      localStorage.setItem('teamsdayMeetings', JSON.stringify(existingMeetings));
      
      setShowScheduleMeetingModal(false);
      setNewMeetingData({
        title: '',
        client: '',
        date: '',
        time: '',
        duration: '60',
        location: '',
        type: 'in-person',
        description: '',
        attendees: ''
      });
      
      // Show success message
      alert('Meeting scheduled successfully and added to calendar!');
    }
  };

  const handleCloseMeetingModal = () => {
    setShowScheduleMeetingModal(false);
    setNewMeetingData({
      title: '',
      client: '',
      date: '',
      time: '',
      duration: '60',
      location: '',
      type: 'in-person',
      description: '',
      attendees: ''
    });
  };

  const markAllAsRead = () => {
    const updatedNotifications = notifications.map(n => ({ ...n, read: true }));
    setNotifications(updatedNotifications);
  };

  const markAsRead = (notificationId: string) => {
    const updatedNotifications = notifications.map(notification => 
      notification.id === notificationId 
        ? { ...notification, read: true }
        : notification
    );
    setNotifications(updatedNotifications);
    
    // Persist to localStorage
    localStorage.setItem('teamsdayNotifications', JSON.stringify(updatedNotifications));
  };

  const handleNotificationClick = (notification: any) => {
    // Mark notification as read
    const updatedNotifications = notifications.map(n => 
      n.id === notification.id ? { ...n, read: true } : n
    );
    setNotifications(updatedNotifications);

    // Navigate based on notification type
    if (window.REACT_APP_NAVIGATE) {
      switch (notification.type) {
        case 'meeting':
          window.REACT_APP_NAVIGATE('/calendar');
          break;
        case 'project':
          if (notification.projectId && notification.category) {
            window.REACT_APP_NAVIGATE(`/projects/details/${notification.category}?projectId=${notification.projectId}`);
          } else {
            window.REACT_APP_NAVIGATE('/projects');
          }
          break;
        case 'deadline':
          if (notification.projectId && notification.category) {
            window.REACT_APP_NAVIGATE(`/projects/details/${notification.category}?projectId=${notification.projectId}`);
          } else {
            window.REACT_APP_NAVIGATE('/projects');
          }
          break;
        case 'invoice':
          if (notification.projectId && notification.category) {
            window.REACT_APP_NAVIGATE(`/projects/details/${notification.category}?projectId=${notification.projectId}`);
          } else {
            window.REACT_APP_NAVIGATE('/projects');
          }
          break;
        case 'task':
          window.REACT_APP_NAVIGATE('/activity');
          break;
        default:
          window.REACT_APP_NAVIGATE('/activity');
      }
    }
    setShowNotifications(false);
  };

  const viewAllNotifications = () => {
    if (window.REACT_APP_NAVIGATE) {
      window.REACT_APP_NAVIGATE('/activity');
    }
    setShowNotifications(false);
  };

  const handleUserMenuAction = (action: string) => {
    setShowUserMenu(false);
    
    if (action === 'profile') {
      if (window.REACT_APP_NAVIGATE) {
        window.REACT_APP_NAVIGATE('/profile');
      }
    } else if (action === 'settings') {
      if (window.REACT_APP_NAVIGATE) {
        window.REACT_APP_NAVIGATE('/settings');
      }
    } else if (action === 'logout') {
      if (confirm('Are you sure you want to logout?')) {
        if (window.REACT_APP_NAVIGATE) {
          window.REACT_APP_NAVIGATE('/');
        }
      }
    }
  };

  return (
    <>
      <header className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <button
              onClick={onMenuClick}
              className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 lg:hidden"
            >
              <i className="ri-menu-line text-xl"></i>
            </button>
            <h1 className="text-xl font-semibold text-gray-900 ml-2 lg:ml-0">Dashboard</h1>
          </div>

          <div className="flex items-center space-x-4">
            {/* Search Bar */}
            <div className="relative">
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"></i>
                <input
                  type="text"
                  placeholder="Search projects, leads, tasks..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-10 pr-4 py-2 w-80 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>

              {/* Search Results Dropdown */}
              {showResults && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
                  {getSearchResults().length > 0 ? (
                    <div className="py-2">
                      {getSearchResults().map((result, index) => (
                        <div
                          key={`${result.category}-${result.id || index}`}
                          className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                          onClick={() => handleSearchResultClick(result)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2">
                                <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                  {result.category}
                                </span>
                                {result.category === 'Projects' && (
                                  <span className={`text-xs px-2 py-1 rounded-full ${
                                    result.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                                    result.status === 'Planning' ? 'bg-yellow-100 text-yellow-800' :
                                    result.status === 'Completed' ? 'bg-green-100 text-green-800' :
                                    'bg-red-100 text-red-800'
                                  }`}>
                                    {result.status}
                                  </span>
                                )}
                                {result.category === 'Tasks' && (
                                  <span className={`text-xs px-2 py-1 rounded-full ${
                                    result.priority === 'High' ? 'bg-red-100 text-red-800' :
                                    result.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-green-100 text-green-800'
                                  }`}>
                                    {result.priority}
                                  </span>
                                )}
                              </div>
                              <p className="font-medium text-gray-900 mt-1">
                                {result.name || result.title}
                              </p>
                              <p className="text-sm text-gray-600">
                                {result.category === 'Projects' && `${result.client} • ${result.budget}`}
                                {result.category === 'Leads' && `${result.company} • ${result.value}`}
                                {result.category === 'Tasks' && result.project}
                              </p>
                            </div>
                            <i className="ri-arrow-right-line text-gray-400"></i>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="px-4 py-8 text-center">
                      <i className="ri-search-line text-3xl text-gray-300 mb-2"></i>
                      <p className="text-gray-500">No results found</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Schedule Meeting Button */}
            <button
              onClick={() => setShowScheduleMeetingModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium whitespace-nowrap"
            >
              <i className="ri-calendar-event-line mr-2"></i>
              Schedule Meeting
            </button>

            {/* Notifications */}
            <div className="relative">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="p-2 text-gray-400 hover:text-gray-500 relative"
              >
                <i className="ri-notification-line text-xl"></i>
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </button>

              {/* Notifications Dropdown */}
              {showNotifications && (
                <div className="absolute top-full right-0 mt-2 w-96 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                  <div className="p-4 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
                      {unreadCount > 0 && (
                        <button
                          onClick={markAllAsRead}
                          className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                        >
                          Mark all as read
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="max-h-96 overflow-y-auto">
                    {notifications.length > 0 ? (
                      <div className="py-2">
                        {notifications.map((notification) => (
                          <div
                            key={notification.id}
                            className={`px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                              !notification.read ? 'bg-blue-50' : ''
                            }`}
                            onClick={() => handleNotificationClick(notification)}
                          >
                            <div className="flex items-start space-x-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${notification.color}`}>
                                <i className={`${notification.icon} text-sm`}></i>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                  <p className={`text-sm font-medium ${!notification.read ? 'text-gray-900' : 'text-gray-700'}`}>
                                    {notification.title}
                                  </p>
                                  {!notification.read && (
                                    <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                                  )}
                                </div>
                                <p className="text-sm text-gray-600 mt-1">
                                  {notification.message}
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                  {notification.time}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="px-4 py-8 text-center">
                        <i className="ri-notification-off-line text-3xl text-gray-300 mb-2"></i>
                        <p className="text-gray-500">No notifications</p>
                      </div>
                    )}
                  </div>

                  <div className="p-4 border-t border-gray-200">
                    <button 
                      onClick={viewAllNotifications}
                      className="w-full text-center text-sm text-blue-600 hover:text-blue-700 font-medium"
                    >
                      View all notifications
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* User Avatar */}
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center hover:bg-gray-400 transition-colors"
              >
                <i className="ri-user-line text-gray-600"></i>
              </button>

              {/* User Menu Dropdown */}
              {showUserMenu && (
                <div className="absolute top-full right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                  <div className="py-2">
                    <div className="px-4 py-2 border-b border-gray-200">
                      <p className="text-sm font-medium text-gray-900">John Doe</p>
                      <p className="text-xs text-gray-500">john@teamsday.com</p>
                    </div>
                    <button
                      onClick={() => handleUserMenuAction('profile')}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                    >
                      <i className="ri-user-line mr-3 text-gray-400"></i>
                      Profile
                    </button>
                    <button
                      onClick={() => handleUserMenuAction('settings')}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                    >
                      <i className="ri-settings-line mr-3 text-gray-400"></i>
                      Settings
                    </button>
                    <div className="border-t border-gray-200 mt-2 pt-2">
                      <button
                        onClick={() => handleUserMenuAction('logout')}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center"
                      >
                        <i className="ri-logout-box-line mr-3 text-red-400"></i>
                        Logout
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Click outside to close dropdowns */}
      {(showResults || showNotifications || showUserMenu) && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => {
            setShowResults(false);
            setShowNotifications(false);
            setShowUserMenu(false);
          }}
        ></div>
      )}

      {/* Schedule Meeting Modal */}
      {showScheduleMeetingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Schedule Meeting</h3>
              <button
                onClick={handleCloseMeetingModal}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Meeting Details */}
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-4">Meeting Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Meeting Title *
                    </label>
                    <input
                      type="text"
                      value={newMeetingData.title}
                      onChange={(e) => setNewMeetingData(prev => ({ ...prev, title: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="Enter meeting title"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Client/Attendee
                    </label>
                    <input
                      type="text"
                      value={newMeetingData.client}
                      onChange={(e) => setNewMeetingData(prev => ({ ...prev, client: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="Enter client name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Meeting Type
                    </label>
                    <select
                      value={newMeetingData.type}
                      onChange={(e) => setNewMeetingData(prev => ({ ...prev, type: e.target.value }))}
                      className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    >
                      <option value="in-person">In-Person</option>
                      <option value="video-call">Video Call</option>
                      <option value="phone-call">Phone Call</option>
                      <option value="site-visit">Site Visit</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date *
                    </label>
                    <input
                      type="date"
                      value={newMeetingData.date}
                      onChange={(e) => setNewMeetingData(prev => ({ ...prev, date: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Time *
                    </label>
                    <input
                      type="time"
                      value={newMeetingData.time}
                      onChange={(e) => setNewMeetingData(prev => ({ ...prev, time: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Duration (minutes)
                    </label>
                    <select
                      value={newMeetingData.duration}
                      onChange={(e) => setNewMeetingData(prev => ({ ...prev, duration: e.target.value }))}
                      className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    >
                      <option value="30">30 minutes</option>
                      <option value="60">1 hour</option>
                      <option value="90">1.5 hours</option>
                      <option value="120">2 hours</option>
                      <option value="180">3 hours</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Location/Link
                    </label>
                    <input
                      type="text"
                      value={newMeetingData.location}
                      onChange={(e) => setNewMeetingData(prev => ({ ...prev, location: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="Enter location or video call link"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Additional Attendees
                    </label>
                    <input
                      type="text"
                      value={newMeetingData.attendees}
                      onChange={(e) => setNewMeetingData(prev => ({ ...prev, attendees: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="Enter email addresses separated by commas"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Meeting Description
                    </label>
                    <textarea
                      value={newMeetingData.description}
                      onChange={(e) => setNewMeetingData(prev => ({ ...prev, description: e.target.value }))}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      placeholder="Enter meeting agenda or description..."
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={handleCloseMeetingModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateMeeting}
                disabled={!newMeetingData.title || !newMeetingData.date || !newMeetingData.time}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap"
              >
                Schedule Meeting
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DashboardHeader;
